<script setup>
import { ref } from "vue";
import MonitoringDetailHeader from "@/features/monitoring/MonitoringDetailHeader.vue";
import MonitoringDetailHeaderBack from "@/features/monitoring/MonitoringDetailHeaderBack.vue";
import MonitoringDetailHeaderClose from "@/features/monitoring/MonitoringDetailHeaderClose.vue";
import MonitoringDetailFooter from "@/features/monitoring/MonitoringDetailFooter.vue";
import BaseInput from "@/library/BaseInput.vue";
import BaseSheet from "@/library/BaseSheet.vue";
import BaseText from "@/library/BaseText.vue";
import BaseButton from "@/library/BaseButton.vue";
import EnrollmentTags from "@/features/enrollment/EnrollmentTags.vue";
import EnrollmentTag from "@/features/enrollment/EnrollmentTag.vue";
import { useNameValidation } from "@/composables/validation/useNameValidation.js";

const names = defineModel("names", { type: Array });

const prefix = ref("");
const suffix = ref("");
const firstName = ref("");
const middleName = ref("");
const lastName = ref("");

const {
  error: firstNameError,
  validate: validateFirstName,
  validateDebounced: validateFirstNameDebounced,
} = useNameValidation(firstName);

const {
  error: middleNameError,
  validate: validateMiddleName,
  validateDebounced: validateMiddleNameDebounced,
} = useNameValidation(middleName, { isRequired: false });

const {
  error: lastNameError,
  validate: validateLastName,
  validateDebounced: validateLastNameDebounced,
} = useNameValidation(lastName);

const validateForm = () => {
  validateFirstName();
  validateMiddleName();
  validateLastName();

  return (
    !firstNameError.value && !middleNameError.value && !lastNameError.value
  );
};

defineExpose({ validateForm });

const onAddName = () => {
  const isValid = validateForm();

  if (isValid) {
    names.value.push({
      prefix: prefix.value,
      first: firstName.value,
      middle: middleName.value,
      last: lastName.value,
      suffix: suffix.value,
    });

    firstName.value = "";
    middleName.value = "";
    lastName.value = "";
    prefix.value = "";
    suffix.value = "";
  }
};

const onRemoveName = (name) => {
  names.value = names.value.filter(
    (existingName) =>
      existingName.first !== name.first ||
      existingName.last !== name.last ||
      existingName.middle !== name.middle ||
      existingName.prefix !== name.prefix ||
      existingName.suffix !== name.suffix
  );
};
</script>

<template>
  <div class="page-monitoring-settings-names">
    <MonitoringDetailHeader>
      <MonitoringDetailHeaderBack to="MonitoringSettings">
        Name
      </MonitoringDetailHeaderBack>
      <MonitoringDetailHeaderClose to="MonitoringSettings" />
    </MonitoringDetailHeader>
    <div class="page-monitoring-settings-names__form">
      <BaseText
        as="h4"
        variant="body-small-medium"
      >
        Please enter your name(s)
      </BaseText>
      <BaseSheet
        spacing-x="sm"
        spacing-y="lg"
        elevation="md"
        class="page-monitoring-settings-names__sheet"
      >
        <BaseInput
          v-model="prefix"
          title="Prefix"
          placeholder="Mr."
          autocomplete="off"
          class="page-monitoring-settings-names__prefix"
          @keydown.enter="onAddName"
        />
        <BaseInput
          v-model="suffix"
          title="Suffix"
          placeholder="Sr."
          autocomplete="off"
          class="page-monitoring-settings-names__suffix"
          @keydown.enter="onAddName"
        />
        <BaseInput
          v-model="firstName"
          :error="firstNameError"
          title="First Name*"
          placeholder="John"
          autocomplete="given-name"
          class="page-monitoring-settings-names__first-name"
          @blur="validateFirstName"
          @input="validateFirstNameDebounced"
          @keydown.enter="onAddName"
        />
        <BaseInput
          v-model="middleName"
          :error="middleNameError"
          title="Middle Name"
          placeholder="William"
          autocomplete="additional-name"
          class="page-monitoring-settings-names__middle-name"
          @blur="validateMiddleName"
          @input="validateMiddleNameDebounced"
          @keydown.enter="onAddName"
        />
        <BaseInput
          v-model="lastName"
          :error="lastNameError"
          title="Last Name*"
          placeholder="Appleseed"
          autocomplete="family-name"
          class="page-monitoring-settings-names__last-name"
          @blur="validateLastName"
          @input="validateLastNameDebounced"
          @keydown.enter="onAddName"
        />
        <EnrollmentTags class="page-monitoring-settings-names__tags">
          <EnrollmentTag
            v-for="(name, index) in names"
            :key="index"
            tabindex="0"
            @keydown.delete="onRemoveName(name)"
            @click="onRemoveName(name)"
          >
            {{
              [name.prefix, name.first, name.middle, name.last, name.suffix]
                .filter(Boolean)
                .join(" ")
            }}
          </EnrollmentTag>
        </EnrollmentTags>
      </BaseSheet>
    </div>
    <MonitoringDetailFooter>
      <BaseButton
        icon="check"
        size="md"
        full-width
        @click="onAddName"
        @keydown.enter="onAddName"
      >
        Add Name
      </BaseButton>
    </MonitoringDetailFooter>
  </div>
</template>

<style lang="scss" scoped>
.page-monitoring-settings-names {
  display: grid;
  row-gap: 24px;
  position: relative;
  height: 100%;
  grid-template-rows: min-content 1fr;

  &__form {
    margin-top: -18px;
  }

  &__sheet {
    display: grid;
    row-gap: 16px;
    column-gap: 6px;
    margin-top: 8px;
    align-items: start;
    grid-template-columns: 1fr 1fr;
    grid-template-areas: "prefix suffix" "first-name first-name" "middle-name middle-name" "last-name last-name";
  }

  &__prefix {
    grid-area: prefix;
  }

  &__suffix {
    grid-area: suffix;
  }

  &__first-name {
    grid-area: first-name;
  }

  &__middle-name {
    grid-area: middle-name;
  }

  &__last-name {
    grid-area: last-name;
  }

  &__tags {
    grid-column: 1/3;
  }
}
</style>
